import React from 'react';
import { Container, Grid, Typography, Box, Breadcrumbs, Link } from "@mui/material";
import { useNavigate } from 'react-router-dom';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HomeIcon from '@mui/icons-material/Home';

const About = () => {
    const navigate = useNavigate();

    return (
        <Container maxWidth="lg" sx={{ my: 5 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    underline="hover"
                    color="inherit"
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </Link>
                <Typography color="text.primary">About</Typography>
            </Breadcrumbs>
            {/* ABOUT US */}
            <Grid container spacing={2} alignItems="center" sx={{ my: 5 }}>
                <Grid item xs="auto">
                    <Box sx={{ width: 55, height: 55 }}>
                        <AccountCircleIcon sx={{ width: '100%', height: '100%', color: '#5871b7' }} />
                    </Box>
                </Grid>
                <Grid item xs>
                    <Typography fontWeight="bold" variant="h5">ABOUT US</Typography>
                    <Typography>
                        Welcome to <i>META BUILD Decarbonization Tool</i> — your decision support tool in deep energy
                        renovations.
                    </Typography>
                </Grid>
            </Grid>

            {/* WHAT WE DO */}
            <Grid container spacing={2} alignItems="center" sx={{ my: 5 }}>
                <Grid item xs="auto">
                    <Box sx={{ width: 55, height: 55 }}>
                        <SettingsSuggestIcon sx={{ width: '100%', height: '100%', color: '#5871b7' }} />
                    </Box>
                </Grid>
                <Grid item xs>
                    <Typography fontWeight="bold" variant="h5">WHAT WE DO</Typography>
                    <Typography>
                        Decarbonization Tool analyzes your building’s data — such as energy consumption, materials and
                        size — and uses advanced optimization algorithms to propose tailored renovation strategies.
                        Whether you're upgrading insulation, installing solar panels, or improving HVAC systems, we help
                        you prioritize actions that deliver the greatest environmental impact for your investment.
                    </Typography>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={12} md={6} sx={{ my: 5 }}>
                    {/* WHY DECARBONIZATION TOOL */}
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs="auto">
                            <Box sx={{ width: 55, height: 55 }}>
                                <HelpOutlineIcon sx={{ width: '100%', height: '100%', color: '#5871b7', mt: '-10px' }} />
                            </Box>
                        </Grid>
                        <Grid item xs>
                            <Typography fontWeight="bold" variant="h5">WHY DECARBONIZATION TOOL?</Typography>
                            <ol>
                                <li>
                                    <Typography>
                                        <strong>Data-Driven Decisions</strong><br /> Our tool is built on proven
                                        environmental
                                        models.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <strong>Budget-Conscious Planning</strong><br /> We tailor recommendations to
                                        what you can
                                        actually afford.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <strong>Sustainability Focused</strong><br /> We prioritize solutions that
                                        maximize CO₂
                                        reductions and long-term energy savings.
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <strong>User-Friendly Interface</strong><br /> No technical background required —
                                        just input
                                        your data and get results.
                                    </Typography>
                                </li>
                            </ol>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={6} sx={{ my: 5 }}>
                    {/* HOW IT WORKS */}
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs="auto">
                            <Box sx={{ width: 55, height: 55 }}>
                                <PrecisionManufacturingIcon
                                    sx={{ width: '100%', height: '100%', color: '#5871b7', mt: '-10px' }} />
                            </Box>
                        </Grid>
                        <Grid item xs>
                            <Typography fontWeight="bold" variant="h5">HOW IT WORKS</Typography>
                            <Typography component="div">
                                <ol>
                                    <li>
                                        <strong>Input Your Building Data</strong><br />
                                        Fill in the forms with details about your building’s structure, energy use, and
                                        available budget.
                                    </li>
                                    <li style={{ marginTop: '0.5em' }}>
                                        <strong>Run the Optimization</strong><br />
                                        Our model evaluates hundreds of possible renovation combinations, considering
                                        costs, carbon savings, and energy performance.
                                    </li>
                                    <li style={{ marginTop: '0.5em' }}>
                                        <strong>Get Smart Recommendations</strong><br />
                                        Receive a personalized renovation plan that outlines the most effective actions
                                        to reduce CO₂ emissions within your financial constraints.
                                    </li>
                                </ol>
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* MISSION */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs="auto">
                    <Box sx={{ width: 55, height: 55, display: 'flex', alignItems: 'center' }}>
                        <VisibilityIcon sx={{ width: '100%', height: '100%', color: '#5871b7' }} />
                    </Box>
                </Grid>
                <Grid item xs>
                    <Box display="flex" alignItems="center">
                        <Box>
                            <Typography fontWeight="bold" variant="h5">MISSION</Typography>
                            <Typography>
                                To empower building owners with data-driven insights that accelerate the transition to a
                                low-carbon future.
                            </Typography>
                        </Box>
                    </Box>
                    <Typography component="div" mt={2}>
                        <ol>
                            <li>
                                <Typography>
                                    Provide actionable solutions on technologies that influence building
                                    performance and carbon footprint.
                                </Typography>
                            </li>
                            <li>
                                <Typography>
                                    Support informed deep energy renovations by providing clear, easy-to-understand
                                    analyses.
                                </Typography>
                            </li>
                            <li>
                                <Typography>
                                    Foster innovation by leveraging cutting-edge optimization technologies.
                                </Typography>
                            </li>
                        </ol>
                    </Typography>
                </Grid>
            </Grid>

        </Container>
    );
};

export default About;
